import { Link } from '@remix-run/react';
import classnames from 'classnames';
import SwiperCore from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import { Flex, useStyles, Button, Label } from '@archipro-design/aria';
import { ArrowNextLine, ArrowPrevLine } from '@archipro-design/icons';
import { useLocalisation } from '@archipro-website/localisation';
import { useTracker } from '@archipro-website/tracker';
import type { TileProps } from '../../type';
import * as S from './RecentlyViewed.style';
import { NarrowContent } from '@modules/articles/component/page-layout/PageLayout.style';
import ArchizeenTile from '~/modules/tile/component/archizeen-tile/ArchizeenImpressionTile';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { getFormattedLocalisedDate } from '@modules/date-time/util/get-localised-date';
import { useTileHotButtons } from '~/modules/root';

// oxlint-disable-next-line react-hooks/rules-of-hooks
SwiperCore.use([Navigation, Pagination]);

const SLIDES_PER_VIEW = 3;

const RecentlyViewedDesktop: React.FC<{ recentlyViewed: TileProps[] }> = ({
    recentlyViewed,
}) => {
    const { css } = useStyles();
    const tracker = useTracker();
    const { localiseDate } = useLocalisation();
    const { getTileTopActions } = useTileHotButtons({ itemType: 'article' });

    return (
        <div className={css(S.Wrap)}>
            <div className={css(NarrowContent)}>
                <Label className={css(S.TitleWrap)} variant="01">
                    RECENTLY VIEWED
                </Label>
                <div className={css(S.SliderWrap)}>
                    <Swiper
                        slidesPerView={SLIDES_PER_VIEW}
                        slidesPerGroup={SLIDES_PER_VIEW}
                        navigation={{
                            nextEl: '.slider-button-next',
                            prevEl: '.slider-button-prev',
                        }}
                        allowSlideNext={true}
                        allowSlidePrev={true}
                        loop={false}
                        className={css(S.Slider)}
                        threshold={40}
                    >
                        {recentlyViewed.map((article) => {
                            const {
                                link,
                                ID,
                                image,
                                title,
                                date,
                                label,
                                size,
                                variant,
                                variables,
                            } = article;

                            const topActions = getTileTopActions({
                                item: article,
                            });

                            return (
                                <SwiperSlide
                                    key={article.ID}
                                    className={css(S.Slide)}
                                >
                                    <div className={css(S.SlideInner)}>
                                        <ArchizeenTile
                                            key={ID}
                                            as={Link}
                                            to={link}
                                            date={getFormattedLocalisedDate(
                                                localiseDate,
                                                date,
                                            )}
                                            label={label}
                                            size={size}
                                            variant={variant}
                                            variables={variables}
                                            prefetch="intent"
                                            onClick={() => {
                                                tracker.log(
                                                    'ArticleRecentlyViewedClick',
                                                    {
                                                        url: new URL(
                                                            window.location
                                                                .href,
                                                        ),
                                                        targetTracker:
                                                            'archiproTracker',
                                                        data: {
                                                            // ArticleID
                                                            ExtraData:
                                                                article.ID,
                                                        },
                                                    },
                                                );
                                            }}
                                            image={{
                                                src: image,
                                                alt: title,
                                                height: 280,
                                                width: 455,
                                                sizes: getImagesSizes(455),
                                            }}
                                            title={title}
                                            impression={{
                                                professionalID:
                                                    article.professionalID,
                                                itemID: ID,
                                                type: 'RecentlyViewed',
                                            }}
                                            topActions={topActions}
                                            articleId={ID}
                                        />
                                    </div>
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>
                    {recentlyViewed.length > SLIDES_PER_VIEW && (
                        <Flex
                            hAlign="end"
                            gap="space-16"
                            className={css(S.SliderControls)}
                        >
                            <Button
                                className={classnames(
                                    css(S.ButtonSlider),
                                    'slider-button-prev',
                                )}
                                size={30}
                                variables={{
                                    iconPadding: 0,
                                }}
                                icon={<ArrowPrevLine />}
                                transparent={true}
                            />
                            <Button
                                className={classnames(
                                    css(S.ButtonSlider),
                                    'slider-button-next',
                                )}
                                size={30}
                                variables={{
                                    iconPadding: 0,
                                }}
                                icon={<ArrowNextLine />}
                                transparent={true}
                            />
                        </Flex>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RecentlyViewedDesktop;
